import { useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { AppBar, Box, IconButton, Toolbar, Tooltip, Typography, useTheme } from '@mui/material'
import LogoutIcon from '@mui/icons-material/Logout'
import { useMobileLogout } from '../../contexts/mobile-logout-context'
import OrganizationIcon from '../OrganizationIcon'
import OnlineStatusIndicator from '../online-status-indicator'
import { getBrandingUrls } from '../../config/market'
import { OfflinePwaMobileMenu } from '../offline/OfflinePwaToolsNav'
import useOnlineStatus from '../../hooks/useOnlineStatus'
import LayoutModeSwitch from '../layout-mode-switch'
import {
  hasValidPersistedSession,
  resolveSessionInit,
  resolveSessionToken,
} from '../../offline/offline-session-utils'

const BRAND = getBrandingUrls()

const MOBILE_ICON_BUTTON_SIZE = 'medium'

/** Spazio sotto AppBar mobile (safe area + altezza toolbar). */
export const mobileTopNavSpacerSx = {
  minHeight: {
    xs: 'calc(56px + env(safe-area-inset-top, 0px))',
    sm: 'calc(64px + env(safe-area-inset-top, 0px))'
  }
}

export function MobileTopNavSpacer() {
  return <Toolbar sx={mobileTopNavSpacerSx} />
}

export default function MobileTopNav({ offlinePwaProps = null }) {
  const theme = useTheme()
  const location = useLocation()
  const { pathname, state } = location
  const token = useMemo(() => resolveSessionToken(state), [state])
  const init = useMemo(() => resolveSessionInit(state), [state])
  const navigate = useNavigate()
  const { _ } = useLingui()

  const { logout, requestLogout } = useMobileLogout()
  const isOnline = useOnlineStatus()
  const hasInitUser = init != null && typeof init === 'object' && init.user != null

  useEffect(() => {
    if (token) return
    if (hasValidPersistedSession()) return
    logout()
  }, [logout, token])

  return (
    <AppBar
      sx={{
        boxShadow: 1,
        backgroundColor: theme.palette.background.default
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.5, sm: 1 },
          px: { xs: 1, sm: 2 },
          pt: 'max(0px, env(safe-area-inset-top, 0px))',
          minHeight: {
            xs: 'calc(56px + env(safe-area-inset-top, 0px))',
            sm: 'calc(64px + env(safe-area-inset-top, 0px))'
          },
          overflow: 'hidden',
          backgroundColor: theme.palette.newColors.gray,
          boxSizing: 'border-box'
        }}
      >
        <Box
          component="img"
          src={BRAND.toolbarWhite}
          alt="ADaMS logo"
          sx={{
            height: { xs: 28, sm: 32 },
            width: 'auto',
            maxWidth: { xs: 84, sm: 120 },
            flexShrink: 1,
            minWidth: 0,
            objectFit: 'contain',
            display: 'block',
            alignSelf: 'center'
          }}
        />

        {pathname === '/home' && offlinePwaProps ? (
          <OfflinePwaMobileMenu
            isOnline={offlinePwaProps.isOnline}
            onExport={offlinePwaProps.onExport}
            onImportZip={offlinePwaProps.onImportZip}
            onPrepareDevice={offlinePwaProps.onPrepareDevice}
            importZipBusy={offlinePwaProps.importZipBusy}
            iconButtonSize={MOBILE_ICON_BUTTON_SIZE}
          />
        ) : null}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.25, sm: 1 },
            ml: 'auto',
            minWidth: 0,
            flexShrink: 0,
            color: theme.palette.text.primary
          }}
        >
          <OnlineStatusIndicator />
          <LayoutModeSwitch compact />

          {hasInitUser ? (
            <>
              <Tooltip title={init.user.username ?? ''} enterTouchDelay={400}>
                <Typography
                  variant="button"
                  component="span"
                  sx={{
                    color: 'white',
                    minWidth: 0,
                    maxWidth: { xs: 56, sm: 88, md: 120 },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flexShrink: 1,
                    display: { xs: 'none', sm: 'inline' },
                    lineHeight: 1.2
                  }}
                >
                  {init.user.username}
                </Typography>
              </Tooltip>
              <IconButton
                size={MOBILE_ICON_BUTTON_SIZE}
                aria-label={_(msg`account of current user`)}
                aria-controls="menu-appbar"
                aria-haspopup="true"
                disabled={!isOnline}
                onClick={() => navigate('/user', { state })}
                sx={{ flexShrink: 0, color: 'white' }}
              >
                <OrganizationIcon
                  isMobile
                  organization={init.user.organization}
                  userType={init.user.type}
                  style={{ color: '#4a4a4a', fontSize: 22 }}
                />
              </IconButton>
            </>
          ) : null}

          <IconButton
            size={MOBILE_ICON_BUTTON_SIZE}
            aria-label={_(msg`logout`)}
            aria-controls="menu-appbar"
            aria-haspopup="false"
            onClick={() => void requestLogout()}
            sx={{ flexShrink: 0, color: 'white' }}
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  )
}
